/*
 * Vedistry Holistic Health Assessment — content
 *
 * Generated from Vedistry_Holistic_Health_Assessment_CONTENT.xlsx (2026-09-14),
 * which is the single source of truth for every string in the quiz.
 *
 * Editing rules:
 *   - Questions, options and their order come from sheet 3. Options score 0..3
 *     left to right, so never reorder them.
 *   - Diet / habits / yoga come from sheet 5 and are stored once per module.
 *   - Band copy and plans come from sheet 6.
 *   - `[NEW]` markers from the guide are recorded in NEEDS_APPROVAL below rather
 *     than inline, so nothing user-facing carries them.
 */
(function () {
  'use strict';

  // Reusable option scales. Index === score.
  var NEVER_DAILY = ['Never', 'Sometimes', 'Often', 'Every day'];
  var NEVER_VERY_OFTEN = ['Never', 'Sometimes', 'Often', 'Very often'];

  function q(id, text, options) {
    return { id: id, text: text, options: options || NEVER_DAILY };
  }

  function plan(primary, supporting) {
    return { primary: primary, supporting: supporting || [] };
  }

  function p(key, name, dose, note) {
    return { key: key, name: name, dose: dose, note: note || null };
  }

  var CONSULT = {
    optional:
      'Optional — if this does not settle over the next few weeks, speak to our expert doctor.',
    recommended:
      'Recommended — we suggest talking this through with our expert doctor.',
    strong:
      'Strongly recommended — please speak to our expert doctor before starting anything.'
  };

  /* ---------------------------------------------------------------------
   * Modules
   * ------------------------------------------------------------------ */

  var modules = {
    dig_ind: {
      label: 'Indigestion',
      chip: 'Indigestion',
      questions: [
        q('dig_ind_1', 'Do you feel heavy or bloated after eating?'),
        q('dig_ind_2', 'Do you feel less hungry than usual?'),
        q('dig_ind_3', 'Do you burp a lot?')
      ],
      diet: {
        dos: [
          'Eat light — stop at about three-quarters full',
          'Moong dal soup, and fresh buttermilk with lunch',
          'Warm, freshly cooked food'
        ],
        donts: [
          'Avoid irregular meal times',
          'Avoid heavy dairy — dahi, paneer, cheese',
          'Avoid caffeine, alcohol and fermented food'
        ]
      },
      habits: {
        dos: [
          'Keep at least 1.5 hours between dinner and sleep',
          'Sleep early and wake early',
          'Walk or jog daily, with yoga or pranayama'
        ],
        donts: [
          'Avoid long night working hours',
          'Avoid eating too fast or too slow',
          "Don't drink a lot of water on an empty stomach"
        ]
      },
      yoga: ['vajrasana', 'pavanamuktasana', 'ustrasana'],
      bands: {
        mild: {
          meaning: 'Your digestion is mostly steady, with the odd heavy day.',
          plan: plan(
            p(
              'dipya-digestive-syrup',
              'Dipya Ayurvedic Digestive Syrup',
              '10 ml twice a day after food, for 1 month.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning:
            'You are getting heaviness and discomfort after meals fairly regularly.',
          plan: plan(
            p(
              'dipya-digestive-syrup',
              'Dipya Ayurvedic Digestive Syrup',
              '10 ml twice a day after food, for 2 months.'
            )
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning:
            'Your digestion is struggling most days, and it is likely shaping how you eat.',
          plan: plan(
            p(
              'dipya-digestive-syrup',
              'Dipya Ayurvedic Digestive Syrup',
              '10 ml twice a day after food, for 2 months.'
            )
          ),
          consultation: CONSULT.strong
        }
      }
    },

    dig_aci: {
      label: 'Acidity',
      chip: 'Acidity',
      questions: [
        q('dig_aci_1', 'Do you get pain or discomfort in your stomach?'),
        q('dig_aci_2', 'Do you get a burning feeling in your chest or stomach?'),
        q('dig_aci_3', 'Does food or acid come back up into your throat?')
      ],
      diet: {
        dos: [
          'Popped rice, moong dal and barley',
          'Meals on time, dinner before 8 pm',
          'Pomegranate, ripe banana and sweet lime'
        ],
        donts: [
          'Avoid deep-fried, oily and processed food',
          'Avoid tea, coffee and cold drinks',
          "Don't drink water right after meals"
        ]
      },
      habits: {
        dos: [
          'Sleep early and wake early',
          'Walk or do light exercise daily',
          'Yoga and pranayama'
        ],
        donts: [
          'Avoid long night working hours',
          "Don't lie down right after eating",
          "Don't keep changing your daily routine"
        ]
      },
      yoga: ['balasana', 'virabhadrasana', 'trikonasana'],
      bands: {
        mild: {
          meaning: 'You get acidity now and then, usually after certain meals.',
          plan: plan(
            p(
              'dipya-acidity-relief',
              'Dipya Acidity Relief Tablet',
              '1 tablet twice a day, half an hour before meals, for 30 days.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning: 'Acidity is showing up often enough that you plan around it.',
          plan: plan(
            p(
              'dipya-acidity-relief',
              'Dipya Acidity Relief Tablet',
              '1 tablet twice a day before meals, for 60 days.'
            ),
            [
              p(
                'dipya-gut-cleanser',
                'Dipya Gut Cleanser',
                '1 tablespoon with warm water at bedtime, for 60 days.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning: 'You are dealing with burning and reflux most days.',
          plan: plan(
            p(
              'dipya-acidity-relief',
              'Dipya Acidity Relief Tablet',
              '1 tablet twice a day before meals, for 90 days.'
            ),
            [
              p(
                'dipya-gut-cleanser',
                'Dipya Gut Cleanser',
                '1 tablespoon with warm water at bedtime, for 90 days.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    },

    dig_con: {
      label: 'Constipation',
      chip: 'Constipation',
      questions: [
        q('dig_con_1', 'Are your stools hard or difficult to pass?'),
        q('dig_con_2', 'Do you feel gas or bloating?'),
        q('dig_con_3', 'Do you feel your bowels are not fully empty?')
      ],
      diet: {
        dos: [
          'Fibre — oats, legumes, nuts, seeds, whole grains',
          'Soaked black raisins, or soaked peanuts with raisins',
          'Plenty of fluids through the day'
        ],
        donts: [
          'Avoid dry, frozen, processed and cold food',
          'Avoid maida — cakes, biscuits, pastries',
          'Avoid dairy other than A2 cow milk, ghee and fresh buttermilk'
        ]
      },
      habits: {
        dos: [
          'Brisk walk and regular exercise',
          'Go to the toilet at the same time each day',
          "Go when you feel the urge — don't hold it"
        ],
        donts: [
          'No phone or newspaper on the toilet',
          "Don't strain",
          'Avoid daytime sleep and late nights'
        ]
      },
      yoga: ['surya-namaskar', 'pavanamuktasana', 'vajrasana'],
      bands: {
        mild: {
          meaning: 'Things are slow some days, but generally moving.',
          plan: plan(
            p(
              'dipya-gut-cleanser',
              'Dipya Gut Cleanser',
              '1–2 tsp at night with lukewarm water, for 1 month.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning: 'Your bowels are not moving as regularly as they should.',
          plan: plan(
            p(
              'dipya-gut-cleanser',
              'Dipya Gut Cleanser',
              '1–2 tsp at night with lukewarm water, for 1 month.'
            )
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning: 'Constipation is persistent and worth addressing properly.',
          plan: plan(
            p(
              'dipya-gut-cleanser',
              'Dipya Gut Cleanser',
              '1–2 tsp at night with lukewarm water, for 3 months.'
            ),
            [
              p(
                'dipya-digestive-care',
                'Dipya Digestive Care',
                '15 ml twice a day after food, for 1 to 3 months.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    },

    dig_ibs: {
      label: 'Irregular bowels / IBS',
      chip: 'Irregular bowels',
      questions: [
        q('dig_ibs_1', 'Do you get sudden loose motions?'),
        q('dig_ibs_2', 'Do you feel bloated?'),
        q(
          'dig_ibs_3',
          'Do your bowels swing between constipation and loose motions?'
        )
      ],
      diet: {
        dos: [
          'Soluble fibre — oats, apples, isabgol',
          'Small, frequent meals',
          'Cooked vegetables rather than raw'
        ],
        donts: [
          'Avoid onion, garlic and beans',
          'Avoid oily and fatty food',
          "Don't eat in a hurry"
        ]
      },
      habits: {
        dos: [
          'Moderate, regular exercise — walking or cycling',
          'Sip water through the day',
          'Fixed meal and sleep times'
        ],
        donts: [
          "Don't overdo heavy exercise",
          "Don't skip meals",
          "Don't push through pain — go gentler instead"
        ]
      },
      // Sheet 5 spells this pose "Pawanmuktasana" for IBS and "Pavanamuktasana"
      // elsewhere. Same pose, normalised to one key so the photo resolves.
      yoga: ['bhujangasana', 'pavanamuktasana', 'vajrasana'],
      bands: {
        mild: {
          meaning: 'Your bowels are unpredictable occasionally, but manageable.',
          plan: plan(
            p(
              'dipya-ibs-care',
              'Dipya IBS Care',
              '1 tablet twice a day after food, for 3 months.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning:
            'The swing between loose and constipated is disrupting your routine.',
          plan: plan(
            p(
              'dipya-ibs-care',
              'Dipya IBS Care',
              '1 tablet twice a day after food, for 3 months.'
            ),
            [
              p(
                'mentacalm',
                'Mentacalm',
                '1 tablet twice a day after food, for 3 months.',
                'If stress is a factor.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning: 'Your digestion is unpredictable most days.',
          plan: plan(
            p(
              'dipya-ibs-care',
              'Dipya IBS Care',
              '1 tablet twice a day after food, for 3 months.'
            ),
            [
              p(
                'mentacalm',
                'Mentacalm',
                '1 tablet twice a day after food, for 3 months.',
                'If stress is a factor.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    },

    dig_pil: {
      label: 'Piles',
      chip: 'Piles',
      questions: [
        q('dig_pil_1', 'Do you notice bleeding when passing stool?', [
          'Never',
          'Once in a while',
          'A few times a week',
          'Almost every day'
        ]),
        q('dig_pil_2', 'Do you have pain or discomfort around the anus?', [
          'None',
          'Mild',
          'Moderate',
          'Severe — affects sitting'
        ]),
        q('dig_pil_3', 'Do you get itching or burning there?', [
          'Never',
          'Once in a while',
          'Often',
          'Every day'
        ]),
        q('dig_pil_4', 'Does a lump come out when you pass stool?', [
          'No',
          'Comes out, goes back on its own',
          'I have to push it back',
          'Stays out'
        ]),
        q(
          'dig_pil_5',
          'Does this affect your daily routine — sitting, working, walking?',
          ['Not at all', 'A little', 'Quite a bit', 'A lot']
        )
      ],
      diet: {
        dos: [
          'Plenty of water through the day',
          'High-fibre food — oats, whole grains, fruit and vegetables',
          'Simple home-cooked food — moong dal, barley, popped rice'
        ],
        donts: [
          'Avoid very spicy and salty food',
          'Avoid deep-fried and processed food',
          'Avoid tea, coffee and cold drinks'
        ]
      },
      habits: {
        dos: [
          'Sitz bath after passing stool',
          'Sleep early and wake early',
          'Walk or do light exercise daily'
        ],
        donts: [
          "Don't strain while passing stool",
          'Avoid sitting for long stretches',
          'Avoid long night working hours'
        ]
      },
      yoga: ['balasana', 'virabhadrasana', 'trikonasana'],
      bands: {
        mild: {
          meaning: 'Early signs, and manageable with the right routine.',
          plan: plan(
            p(
              'dipya-pylecalm-tablet',
              'Dipya Pylecalm Tablet',
              '1 tablet twice a day after meals, for 30 days.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning: 'Symptoms are regular enough to be affecting your day.',
          plan: plan(
            p(
              'dipya-pylecalm-tablet',
              'Dipya Pylecalm Tablet',
              '1 tablet twice a day after meals, for 30 days.'
            ),
            [
              p(
                'dipya-pylecalm-ointment',
                'Dipya Pylecalm Ointment',
                'Applied after passing stool, for 60 days.'
              ),
              p(
                'dipya-gut-cleanser',
                'Dipya Gut Cleanser',
                'At bedtime, for 60 days.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning: 'This is significantly affecting your comfort and routine.',
          plan: plan(
            p(
              'dipya-pylecalm-tablet',
              'Dipya Pylecalm Tablet',
              '1 tablet twice a day after meals, for 30 days.'
            ),
            [
              p(
                'dipya-pylecalm-ointment',
                'Dipya Pylecalm Ointment',
                'Applied after passing stool, for 60 days.'
              ),
              p(
                'dipya-gut-cleanser',
                'Dipya Gut Cleanser',
                'At bedtime, for 60 days.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    },

    jnt: {
      label: 'Joints and movement',
      chip: 'Joints',
      questions: [
        q('jnt_1', 'How much joint pain do you have right now?', [
          'None',
          'Some days',
          'Most days',
          'Every day'
        ]),
        q('jnt_2', 'Do your knees feel unsteady or give way while walking?', [
          'Never',
          'Sometimes',
          'Often',
          'Always'
        ]),
        q('jnt_3', 'Which of these is hardest for you right now?', [
          'Nothing is difficult',
          'Getting up from a chair',
          'Climbing stairs',
          'Walking'
        ]),
        q('jnt_4', 'Do your joints make a popping or cracking sound?', [
          'Never',
          'Rarely',
          'Often',
          'Every time'
        ])
      ],
      diet: {
        dos: [
          'Omega-3 fats — olive oil, flaxseed, walnut',
          'Millets and grains — oats, jowar, bajra, ragi',
          'Calcium — milk, eggs, makhana, anjeer'
        ],
        donts: [
          'Avoid excess sugar, salt, fried and packaged food',
          'Avoid dahi',
          'Avoid tobacco, alcohol and coffee'
        ]
      },
      habits: {
        dos: [
          'Daily walk, up to about 3.5 km, without overdoing it',
          'Simple strengthening — hamstring stretch, straight leg raise, heel raise',
          'Keep a healthy weight, and use support for affected joints'
        ],
        donts: [
          'Avoid activities that strain affected joints',
          "Don't sit in one position for long",
          "Don't stop moving on painful days — move gently instead"
        ]
      },
      yoga: ['balasana', 'marjaryasana', 'badhakonasana'],
      bands: {
        mild: {
          meaning:
            'Some stiffness and the occasional ache, but nothing limiting you.',
          plan: plan(
            p(
              'go365-nutra',
              'Go365 Nutra Tablet',
              '1 tablet three times a day after food, for 3 months.'
            ),
            [
              p(
                'go365-topical',
                'Go365 Spray or Ointment',
                'On the affected area twice a day, for 1 month.'
              )
            ]
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning: 'Joint pain is showing up regularly and shaping what you do.',
          plan: plan(
            p(
              'go365-nutra',
              'Go365 Nutra Tablet',
              '1 tablet three times a day after food, for 3 months.'
            ),
            [
              p(
                'go365-topical',
                'Go365 Spray or Ointment',
                'Twice a day, for 3 months.'
              ),
              p(
                'shallaki-plus',
                'Shallaki Plus',
                '1 tablet twice a day after food, for 3 months.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning: 'Your joints are limiting your movement most days.',
          plan: plan(
            p(
              'go365-nutra',
              'Go365 Nutra Tablet',
              '1 tablet three times a day after food, for 3 months.'
            ),
            [
              p(
                'go365-topical',
                'Go365 Spray or Ointment',
                'Twice a day, for 3 months.'
              ),
              p(
                'shallaki-plus',
                'Shallaki Plus',
                '1 tablet twice a day after food, for 3 months.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    },

    slp: {
      label: 'Sleep',
      chip: 'Sleep',
      questions: [
        q('slp_1', 'How long does it take you to fall asleep?', [
          'I fall asleep easily',
          "A little longer than I'd like",
          'Quite a while',
          "Very long, or I don't sleep"
        ]),
        q('slp_2', 'Do you wake up during the night?', [
          'Never or rarely',
          'Now and then',
          'Often',
          'Every night'
        ]),
        q('slp_3', 'Do you wake up earlier than you want to?', [
          'No',
          'A little earlier',
          'Much earlier',
          'Very early, or I barely sleep'
        ]),
        q('slp_4', 'How is the quality of your sleep?', [
          'Good',
          'Not great',
          'Poor',
          'Very poor'
        ]),
        q('slp_5', 'Does poor sleep affect your day?', [
          'Not at all',
          'A little',
          'Quite a lot',
          'It affects everything'
        ])
      ],
      diet: {
        dos: [
          'Magnesium-rich food — nuts, seeds, avocado, fish',
          'Keep dinner light',
          'Finish dinner at least 1.5 hours before bed'
        ],
        donts: [
          'No tea or coffee after 6 pm',
          'Avoid heavy or very spicy food at night',
          'Avoid alcohol before bed'
        ]
      },
      habits: {
        dos: [
          'Daily meditation or pranayama',
          'Fixed sleep and wake time',
          'Wind down with something calming before bed'
        ],
        donts: [
          'Avoid screens before bedtime',
          'Avoid long daytime naps',
          'Avoid heavy exercise late at night'
        ]
      },
      yoga: ['janu-sirsasana', 'supta-baddha-konasana', 'makarasana'],
      bands: {
        mild: {
          meaning: 'You sleep reasonably well, with the occasional rough night.',
          plan: plan(
            p(
              'zzowin',
              'Zzowin Tablet',
              '1 tablet 60 minutes before bed, for 1 month.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning:
            'Your sleep is being disrupted often enough to show up in your day.',
          plan: plan(
            p(
              'zzowin',
              'Zzowin Tablet',
              '1 tablet 60 minutes before bed, for 1 month.'
            ),
            [
              p(
                'ashwagandha-plus',
                'Ashwagandha Plus',
                '1 tablet twice a day after food.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning:
            'You are not getting the sleep you need, and it is affecting how you function.',
          plan: plan(
            p(
              'zzowin',
              'Zzowin Tablet',
              '1 tablet 60 minutes before bed, for 1 month.'
            ),
            [
              p(
                'ashwagandha-plus',
                'Ashwagandha Plus',
                '1 tablet twice a day after food.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    },

    str: {
      label: 'Stress and anxiety',
      chip: 'Stress',
      questions: [
        q('str_1', 'How often do you feel anxious or worried?', NEVER_VERY_OFTEN),
        q('str_2', 'Do you find it hard to relax?', NEVER_VERY_OFTEN),
        q(
          'str_3',
          'Do you get a racing heartbeat, chest tightness or restlessness?',
          NEVER_VERY_OFTEN
        ),
        q(
          'str_4',
          'Do you feel unable to keep up with everything you have to do?',
          NEVER_VERY_OFTEN
        ),
        q(
          'str_5',
          'Do you feel unable to control irritation or anger?',
          NEVER_VERY_OFTEN
        )
      ],
      diet: {
        dos: [
          'Herbal tea — it helps you feel calmer',
          'Whole grains through the day',
          'Eat at regular times'
        ],
        donts: [
          'Avoid excess sugar, salt, fried and packaged food',
          'Avoid tobacco, alcohol and coffee',
          "Don't skip meals"
        ]
      },
      habits: {
        dos: [
          'Fixed sleep and wake time',
          'Walk or run regularly',
          'Deep breathing, yoga or meditation'
        ],
        donts: [
          'Avoid tobacco, alcohol and excess caffeine',
          'Avoid working late into the night',
          "Don't keep it to yourself — talk to someone"
        ]
      },
      yoga: ['balasana', 'uttanasana', 'viparita-karani'],
      bands: {
        mild: {
          meaning: 'Some pressure, but you are largely managing it.',
          plan: plan(
            p('mentacalm', 'Mentacalm', '1 tablet twice a day, for 2 months.')
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning: 'Stress is showing up regularly and taking a toll.',
          plan: plan(
            p('mentacalm', 'Mentacalm', '1 tablet twice a day, for 2 months.'),
            [
              p(
                'ashwagandha-plus',
                'Ashwagandha Plus',
                '1 tablet twice a day, for 2 months.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning:
            'You are carrying a lot right now, and it is affecting how you feel day to day.',
          plan: plan(
            p('mentacalm', 'Mentacalm', '1 tablet twice a day, for 3 months.'),
            [
              p(
                'ashwagandha-plus',
                'Ashwagandha Plus',
                '1 tablet twice a day, for 3 months.'
              )
            ]
          ),
          planNote: 'Stress management counselling is also advised.',
          consultation: CONSULT.strong
        }
      }
    },

    cog: {
      label: 'Memory and focus',
      chip: 'Memory and focus',
      questions: [
        q(
          'cog_1',
          'Do you lose track of what is going on around you?',
          NEVER_VERY_OFTEN
        ),
        q(
          'cog_2',
          'Do you find it hard to take in new information?',
          NEVER_VERY_OFTEN
        ),
        q('cog_3', 'Do you find it hard to concentrate?', NEVER_VERY_OFTEN),
        q('cog_4', 'Do you forget recent things?', NEVER_VERY_OFTEN),
        q(
          'cog_5',
          'Do you feel less sharp or alert than before?',
          NEVER_VERY_OFTEN
        )
      ],
      diet: {
        dos: [
          '1–2 walnuts and almonds every day',
          'Omega-3 — fatty fish, or flaxseed, chia and walnuts',
          'Berries and pumpkin seeds'
        ],
        donts: ['Avoid excess sugar', "Don't skip meals", 'Limit alcohol']
      },
      habits: {
        dos: [
          'Brisk walk, jog, cycle or swim — 30 minutes, 3 times a week',
          'Deep breathing and yoga',
          'Keep a fixed sleep schedule'
        ],
        donts: [
          'Avoid doing several things at once',
          'Avoid long screen stretches without a break',
          'Avoid late nights'
        ]
      },
      yoga: ['navasana', 'padmasana', 'vajrasana'],
      bands: {
        mild: {
          meaning: 'The odd lapse in focus, nothing out of the ordinary.',
          plan: plan(
            p(
              'memosharp',
              'Memosharp Tablet',
              '1 tablet twice a day, for 2 months.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning:
            'You are noticing gaps in focus and recall often enough to be frustrating.',
          plan: plan(
            p(
              'memosharp',
              'Memosharp Tablet',
              '1 tablet twice a day, for 3 months.'
            )
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning: 'Concentration and memory are affecting your day to day.',
          plan: plan(
            p(
              'memosharp',
              'Memosharp Tablet',
              '1 tablet twice a day, for 3 months.'
            )
          ),
          consultation: CONSULT.strong
        }
      }
    },

    imm: {
      label: 'Immunity',
      chip: 'Immunity',
      questions: [
        q(
          'imm_1',
          'How often do you get cold, cough, sore throat or fever?',
          NEVER_VERY_OFTEN
        ),
        q(
          'imm_2',
          'Do small illnesses or cuts take a long time to heal?',
          NEVER_VERY_OFTEN
        ),
        q('imm_3', 'How are your energy levels for daily work?', [
          'Excellent',
          'Good',
          'Fair',
          'Poor'
        ]),
        q(
          'imm_4',
          'Do you get seasonal allergies — sneezing, runny nose, skin reactions?',
          NEVER_VERY_OFTEN
        ),
        q('imm_5', 'How steady has your mood been in the last month?', [
          'Always good',
          'Mostly good',
          'Up and down',
          'Poor'
        ])
      ],
      diet: {
        dos: [
          'Leafy greens, colourful vegetables, citrus and berries',
          'Lean protein and healthy fats — eggs, fish, legumes, nuts',
          'Turmeric with black pepper, ginger and garlic in cooking'
        ],
        donts: [
          'Limit processed, high-sugar and high-fat food',
          'Avoid cold and stale food',
          "Don't skip water — stay hydrated"
        ]
      },
      habits: {
        dos: [
          '7–9 hours of sleep',
          '30 minutes of brisk walking or yoga',
          'Daily meditation or pranayama, and time outdoors'
        ],
        donts: [
          'Avoid smoking and excess alcohol',
          'Avoid late nights',
          "Don't let stress build up without a break"
        ]
      },
      yoga: ['uttanasana', 'halasana', 'savasana'],
      bands: {
        mild: {
          meaning: 'Your immunity is holding up reasonably well.',
          plan: plan(
            p(
              'kofol-immunity',
              'Kofol Immunity Tablets',
              '1–2 tablets twice a day after meals, for 3 months.'
            )
          ),
          consultation: CONSULT.optional
        },
        moderate: {
          meaning:
            'You are catching things more often than you should, and recovering slowly.',
          plan: plan(
            p(
              'kofol-immunity',
              'Kofol Immunity Tablets',
              '1–2 tablets twice a day after meals, for 3 months.'
            ),
            [
              p(
                'vedistry-prash',
                'Vedistry Prash',
                '½ to 1 tablespoon once a day.'
              )
            ]
          ),
          consultation: CONSULT.recommended
        },
        severe: {
          meaning:
            'You are getting run down frequently and taking a long time to bounce back.',
          plan: plan(
            p(
              'kofol-immunity',
              'Kofol Immunity Tablets',
              '1–2 tablets twice a day after meals, for 3 months.'
            ),
            [
              p(
                'vedistry-prash',
                'Vedistry Prash',
                '½ to 1 tablespoon once a day for 3 months.'
              ),
              p(
                'amla-plus',
                'Amla Plus',
                '1 tablet twice a day for 3 months.'
              )
            ]
          ),
          consultation: CONSULT.strong
        }
      }
    }
  };

  // Every question carries max = 3 per option, so max is derived, not authored.
  // This keeps sheet 4's "Maximum" column from drifting out of sync with sheet 3.
  Object.keys(modules).forEach(function (key) {
    var mod = modules[key];
    mod.id = key;
    mod.max = mod.questions.length * 3;
  });

  /* ---------------------------------------------------------------------
   * Yoga library — keyed on the pose name used in sheet 5.
   * Images are supplied by design and wired through section settings.
   * ------------------------------------------------------------------ */

  var yoga = {
    vajrasana: 'Vajrasana',
    pavanamuktasana: 'Pavanamuktasana',
    ustrasana: 'Ustrasana',
    balasana: 'Balasana',
    virabhadrasana: 'Virabhadrasana',
    trikonasana: 'Trikonasana',
    'surya-namaskar': 'Surya Namaskar',
    bhujangasana: 'Bhujangasana',
    marjaryasana: 'Marjaryasana',
    badhakonasana: 'Badhakonasana',
    'janu-sirsasana': 'Janu Sirsasana',
    'supta-baddha-konasana': 'Supta Baddha Konasana',
    makarasana: 'Makarasana',
    uttanasana: 'Uttanasana',
    'viparita-karani': 'Viparita Karani',
    navasana: 'Navasana',
    padmasana: 'Padmasana',
    halasana: 'Halasana',
    savasana: 'Savasana'
  };

  /* ---------------------------------------------------------------------
   * Export
   * ------------------------------------------------------------------ */

  /*
   * Dial codes offered beside the mobile field. The first entry is the default.
   *
   * `digits` is the national number length the form enforces. Every row is 10
   * as specified. Some numbering plans are shorter — UAE mobiles are 9, for
   * instance — so if a market is added where 10 is wrong, correct that one
   * number here; no code change is needed.
   */
  var countries = [
    { name: 'India', code: '+91', digits: 10 },
    { name: 'United States', code: '+1', digits: 10 },
    { name: 'Canada', code: '+1', digits: 10 },
    { name: 'United Kingdom', code: '+44', digits: 10 },
    { name: 'UAE', code: '+971', digits: 10 },
    { name: 'Saudi Arabia', code: '+966', digits: 10 },
    { name: 'Qatar', code: '+974', digits: 10 },
    { name: 'Kuwait', code: '+965', digits: 10 },
    { name: 'Oman', code: '+968', digits: 10 },
    { name: 'Bahrain', code: '+973', digits: 10 },
    { name: 'Singapore', code: '+65', digits: 10 },
    { name: 'Malaysia', code: '+60', digits: 10 },
    { name: 'Australia', code: '+61', digits: 10 },
    { name: 'New Zealand', code: '+64', digits: 10 },
    { name: 'Nepal', code: '+977', digits: 10 },
    { name: 'Sri Lanka', code: '+94', digits: 10 },
    { name: 'Bangladesh', code: '+880', digits: 10 },
    { name: 'South Africa', code: '+27', digits: 10 },
    { name: 'Germany', code: '+49', digits: 10 },
    { name: 'France', code: '+33', digits: 10 }
  ];

  window.VedistryAssessmentContent = {
    version: '2026-09-14',
    countries: countries,

    concerns: [
      {
        id: 'dig',
        title: 'Digestion',
        sub: 'Gas, acidity, constipation, piles',
        children: [
          { id: 'dig_ind', title: 'Indigestion and heaviness' },
          { id: 'dig_aci', title: 'Acidity and heartburn' },
          { id: 'dig_con', title: 'Constipation' },
          { id: 'dig_ibs', title: 'Irregular bowels (IBS)' },
          { id: 'dig_pil', title: 'Piles' }
        ]
      },
      {
        id: 'jnt',
        title: 'Joints and movement',
        sub: 'Pain, stiffness, difficulty walking',
        module: 'jnt'
      },
      {
        id: 'slp',
        title: 'Sleep',
        sub: 'Trouble falling or staying asleep',
        module: 'slp'
      },
      {
        id: 'str',
        title: 'Stress and anxiety',
        sub: 'Worry, restlessness, irritation',
        module: 'str'
      },
      {
        id: 'cog',
        title: 'Memory and focus',
        sub: 'Forgetfulness, poor concentration',
        module: 'cog'
      },
      {
        id: 'imm',
        title: 'Immunity',
        sub: 'Frequent cold, cough, low energy',
        module: 'imm'
      }
    ],

    modules: modules,
    yoga: yoga,

    // Sheet 4. `question` triggers compare a single answer's score;
    // `score` triggers compare a module percentage.
    redFlags: [
      { type: 'question', id: 'dig_pil_1', eq: 3, meaning: 'Bleeding almost every day' },
      { type: 'question', id: 'dig_pil_4', eq: 3, meaning: 'Lump stays outside and cannot be pushed back' },
      { type: 'question', id: 'jnt_3', eq: 3, meaning: 'Difficulty walking' },
      { type: 'score', module: 'str', gt: 80, meaning: 'Very high stress score' },
      { type: 'score', module: '*', gte: 100, meaning: 'Maximum score on every question' }
    ],

    bands: [
      { id: 'mild', label: 'Mild', max: 30 },
      { id: 'moderate', label: 'Moderate', max: 60 },
      { id: 'severe', label: 'Severe', max: Infinity }
    ],

    // Sheet 2. Anything a merchant is likely to reword is also exposed as a
    // section setting, which overrides these defaults.
    copy: {
      introHeadline: 'Vedistry Holistic Health Assessment',
      introBody:
        'Start your wellness journey with the Vedistry Holistic Health Assessment — an Ayurvedic evaluation created by our certified experts. Answer a few simple questions about how you have been feeling. You will get a clear picture of where you stand, along with diet, lifestyle and Ayurvedic guidance made for you.',
      introBadge: 'Takes about 2 minutes',
      introButton: 'Start assessment',
      introSmallPrint: 'Free, and completely private.',
      disclaimer: 'This is a self-assessment, not a medical diagnosis.',
      resultDisclaimer:
        'This is a self-assessment, not a medical diagnosis. Please speak to a qualified practitioner before starting any product.',
      concernHeadline: 'What would you like to check today?',
      concernSubline:
        'Choose the one that troubles you most. You can check another area afterwards.',
      subConcernHeadline: 'Which of these sounds most like you?',
      questionHint: 'Tap an answer to continue',
      backLabel: 'Back',
      detailsHeadline: 'Almost there. Where should we send your plan?',
      detailsSubline:
        'We will use this only to prepare and send your assessment.',
      countryLabel: 'Country code',
      detailsConsent:
        'I agree to Vedistry contacting me with my assessment and related guidance.',
      detailsButton: 'See my results',
      resultGreeting: 'Hello {name},',
      resultSubline: 'here is what your answers tell us.',
      planHeading: 'Your expert recommended plan',
      tipsHeading: 'Your lifestyle guidance',
      dietLabel: 'Diet',
      habitsLabel: 'Daily habits',
      yogaHeading: 'Yoga that helps',
      consultHeadline: 'Your health deserves personal attention',
      consultBody:
        'Understand your dosha and get personalised Ayurvedic guidance from our expert doctors.',
      consultButton: 'Book free consultation',
      aboutHeadline: 'Why Vedistry',
      aboutBody:
        'Vedistry blends the depth of classical Ayurveda with modern science — clinically tested formulations, developed by Ayurvedic doctors and researchers, trusted by 2.5 million+ customers.',
      trustBar: [
        { value: '7+', label: 'Decades of legacy' },
        { value: '5L+', label: 'Doctors' },
        { value: '3', label: 'WHO-GMP facilities' },
        { value: '30+', label: 'Countries' }
      ],
      shareButton: 'Share on WhatsApp',
      retakeButton: 'Check another concern',
      reportButton: 'Download report',
      shareText:
        'I just took the Vedistry Holistic Health Assessment — it took 2 minutes and gave me a clear plan. Try it: vedistry.com/pages/health-assessment',
      addToCart: 'Add to cart',
      soldOut: 'Sold out',

      // Neither the workbook nor the guide supplies these two. Authored here so
      // the flow is complete; both are overridable from section settings and
      // both need medical/legal sign-off before go-live.
      screeningHeadline: 'One last check',
      screeningQuestion: 'Are you currently pregnant or breastfeeding?',
      screeningYes: 'Yes',
      screeningNo: 'No',
      screeningSkip: 'Prefer not to say',
      minorHeadline: 'This assessment is for adults',
      minorBody:
        'This assessment is designed for people aged 18 and over. Please speak to a qualified practitioner who can advise you directly.'
    },

    // Guide sheet 1: content added that was not in the original workbook and
    // needs medical approval before it goes live. Tracked, not rendered.
    NEEDS_APPROVAL: [
      'dig_pil diet dos 1-2',
      'dig_pil habits donts 1-2',
      'jnt habits donts 2-3',
      'slp diet dos 2-3, slp diet donts 2-3',
      'slp habits donts 2-3',
      'str diet dos 3, str diet donts 3',
      'str habits donts 2-3',
      'cog diet donts 1-3, cog habits donts 1-3, cog yoga vajrasana',
      'imm diet donts 2, imm habits donts 2-3',
      'copy.screening* and copy.minor* (authored — absent from both source files)'
    ]
  };
})();
